import { Box } from '@mui/material';
import React, { forwardRef, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import OLShippingDetailsContainer from '../../../containers/ShippingDetailsContainer/OneLink/OneLink';
import {
  getInitialSelectorState,
  resolveOfficeDropshipEnabled,
} from '../../../containers/ShippingDetailsContainer/utils';
import { IOneLinkChunkFormProps } from '../../../contexts/OneLinkSummaryProvider';
import { selectAddressBook } from '../../../store/selectors/addressBook';
import { IOneLinkShippingDetails } from '../../../types/shipping';
import { isAllOrgAddressesActive } from '../../../utils/addressBook';
import { isObjectEmpty, isObjectsEqual } from '../../../utils/helpers';
import { FormControls } from '../../SummaryComponentMUI/components/common';

const ShippingForm = forwardRef<boolean | null, IOneLinkChunkFormProps>(({ onSubmit, instance, onClose }, ref) => {
  const addresses = useSelector(selectAddressBook);

  const initialShippingValues = useMemo<IOneLinkShippingDetails>(() => {
    const isReceiverAddressFixed = instance?.is_receiver_address_fixed ?? false;
    const receiverAddress = instance?.receiver_fixed_address;
    const orgAddressIds = instance?.org_address_ids;

    return {
      is_receiver_address_fixed: isReceiverAddressFixed,
      receiver_address: receiverAddress,
      org_address_ids: orgAddressIds,
      // Resolve rather than trusting the stored flag: the toggle only exists alongside an Address
      // Book selection, so a saved One-Link without one must not seed a truthy flag back in.
      is_office_dropship_enabled:
        resolveOfficeDropshipEnabled({
          // One-Link is always delayed shipping.
          selectValue: getInitialSelectorState({
            is_receiver_address_fixed: isReceiverAddressFixed,
            is_delayed_shipping: true,
            address: receiverAddress,
          }),
          org_address_ids: orgAddressIds,
          receiver_address: receiverAddress,
          is_office_dropship_enabled: instance?.is_office_dropship_enabled ?? false,
        }) ?? false,
    };
  }, [instance]);

  const [shipping, setShipping] = React.useState<IOneLinkShippingDetails>(initialShippingValues);

  const handleChange = useCallback((v: IOneLinkShippingDetails) => {
    setShipping(v);
  }, []);

  const handleSubmit = useCallback(() => {
    const { receiver_address, ...rest } = shipping;

    onSubmit({
      shipping_info: {
        ...rest,
        receiver_fixed_address: receiver_address,
      },
    });

    if (ref && typeof ref !== 'function') {
      ref.current = false;
    }
  }, [ref, shipping, onSubmit]);

  const handleReset = useCallback(() => {
    setShipping(initialShippingValues);
    onClose();
  }, [onClose, initialShippingValues]);

  const isValidForm = useMemo(() => {
    const { receiver_address, org_address_ids, is_receiver_address_fixed } = shipping;

    const areOrgAddressesActive =
      org_address_ids && org_address_ids.length > 0 ? isAllOrgAddressesActive(addresses, org_address_ids) : true;

    // Recipient Decides with addresses
    if (!is_receiver_address_fixed && org_address_ids?.length) {
      return areOrgAddressesActive;
    }

    // Fixed address and Recipient Decides from List Only
    if (is_receiver_address_fixed) {
      const hasValidOrgAddresses = org_address_ids && org_address_ids.length >= 2 && areOrgAddressesActive;
      const hasValidFixedAddress = receiver_address && !isObjectEmpty(receiver_address);

      return hasValidOrgAddresses || hasValidFixedAddress;
    }

    return true;
  }, [shipping, addresses]);

  const isValuesEqual = useMemo(
    () => isObjectsEqual(initialShippingValues, shipping),
    [initialShippingValues, shipping],
  );

  useEffect(() => {
    if (ref && typeof ref !== 'function') {
      ref.current = !isValuesEqual;
    }
  }, [isValuesEqual]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
      <OLShippingDetailsContainer mui values={shipping} onChange={handleChange} maxHeightList />
      <FormControls onCancel={handleReset} onSubmit={handleSubmit} isDisabled={!isValidForm || isValuesEqual} />
    </Box>
  );
});

export default ShippingForm;
